import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Log {
  id: number
  header: string | null
  log: string | null
  notes: string | null
  log_date: string | null
  scheduled_date: string | null
  scheduled_notes: string | null
  log_type: string | null
  log_type_id: number | null
  location: string | null
  customer_location_id: number | null
  contact: string | null
  customer_user_id: number | null
  user: string | null
  user_id: number | null
  status: number
  urgent: boolean
  completed: boolean
  completed_by: string | null
  completed_on: string | null
  created_at: string
}

interface Props {
  customer: Customer
  log: Log
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return date.toLocaleString()
}

export default function Show({ customer, log }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Logs", href: `/customers/${customer.id}/logs` },
    {
      title: log.header ?? "Log",
      href: `/customers/${customer.id}/logs/${log.id}`,
    },
  ]

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this log?")) {
      router.delete(`/customers/${customer.id}/logs/${log.id}`)
    }
  }

  const handleComplete = () => {
    if (confirm("Mark this log as complete?")) {
      router.patch(`/customers/${customer.id}/logs/${log.id}/complete`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${log.header ?? "Log"} - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">
              {log.header ?? "(No subject)"}
              {log.urgent && (
                <span className="bg-hpi-red/20 text-hpi-red ml-2 rounded px-2 py-1 text-sm">
                  Urgent
                </span>
              )}
            </h1>
            <p className="text-hpi-med-blue">
              {log.log_type && <span className="mr-4">{log.log_type}</span>}
              {log.log_date && (
                <span>Date: {formatDateTime(log.log_date)}</span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              href={`/customers/${customer.id}/logs/${log.id}/edit`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Edit
            </Link>
            {!log.completed && (
              <button
                onClick={handleComplete}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
              >
                Mark Complete
              </button>
            )}
            <button
              onClick={handleDelete}
              className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
            >
              Delete
            </button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Log Details</h2>
            {log.log && (
              <div className="mb-4">
                <h3 className="text-hpi-med-blue font-medium">Log</h3>
                <p className="whitespace-pre-wrap">{log.log}</p>
              </div>
            )}
            {log.notes && (
              <div className="mb-4">
                <h3 className="text-hpi-med-blue font-medium">Notes</h3>
                <p className="whitespace-pre-wrap">{log.notes}</p>
              </div>
            )}
          </div>

          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Information</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
              <dt className="text-hpi-med-blue font-medium">Assigned To</dt>
              <dd>{log.user ?? "-"}</dd>
              {log.location && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Location</dt>
                  <dd>{log.location}</dd>
                </>
              )}
              {log.contact && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Contact</dt>
                  <dd>{log.contact}</dd>
                </>
              )}
              <dt className="text-hpi-med-blue font-medium">Created</dt>
              <dd>{formatDateTime(log.created_at)}</dd>
            </dl>
          </div>

          {log.scheduled_date && (
            <div className="border-hpi-yellow bg-hpi-yellow/20 rounded border p-4">
              <h2 className="mb-4 text-lg font-semibold">Scheduled</h2>
              <p className="mb-2">
                <strong>Date:</strong> {formatDateTime(log.scheduled_date)}
              </p>
              {log.scheduled_notes && (
                <div>
                  <strong>Notes:</strong>
                  <p className="whitespace-pre-wrap">{log.scheduled_notes}</p>
                </div>
              )}
            </div>
          )}

          {log.completed && (
            <div className="border-hpi-dark-green bg-hpi-dark-green/10 rounded border p-4">
              <h2 className="mb-4 text-lg font-semibold">Completed</h2>
              <p>
                <strong>By:</strong> {log.completed_by ?? "Unknown"}
              </p>
              <p>
                <strong>On:</strong> {formatDateTime(log.completed_on)}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6">
          <Link
            href={`/customers/${customer.id}/logs`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Logs
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
